/**
 * Urban Brain Health Model — Main assessment engine.
 *
 * Assesses zones (districts, corridors, neighborhoods, campuses) for brain health
 * impact using urban-scale indicators mapped to the same 8 pathways.
 */

import * as p from './parameters';
import {
  calculateMovement, calculateNature, calculateDaylight, calculateEnrichment,
  calculateWayfinding, calculateAirQuality, calculateNoise, calculateVisualEnvironment,
  calculatePathwaySynergy, calculateRegionalScores, clamp,
} from './pathways';

const POSITIVE_PATHWAYS = ['movement', 'nature', 'daylight', 'enrichment', 'wayfinding'];
const NEGATIVE_PATHWAYS = ['air_quality', 'noise', 'visual'];

/**
 * Assess a single zone for urban brain health.
 * @param {Object} inputs - Urban indicator measurements
 * @returns {Object} Assessment result with scores and interpretation
 */
export function assessZone(inputs = {}) {
  const d = p.DEFAULTS;

  // Apply defaults
  const walkScore = inputs.walkScore ?? d.walkScore;
  const bikeInfrastructure = inputs.bikeInfrastructure ?? d.bikeInfrastructure;
  const blockPermeability = inputs.blockPermeability ?? d.blockPermeability;
  const treeCanopyPct = inputs.treeCanopyPct ?? d.treeCanopyPct;
  const greenSpaceAccess300m = inputs.greenSpaceAccess300m ?? d.greenSpaceAccess300m;
  const permeableSurfacePct = inputs.permeableSurfacePct ?? d.permeableSurfacePct;
  const skyViewFactor = inputs.skyViewFactor ?? d.skyViewFactor;
  const sunlightHoursGroundPlane = inputs.sunlightHoursGroundPlane ?? d.sunlightHoursGroundPlane;
  const pm25Annual = inputs.pm25Annual ?? d.pm25Annual;
  const pctZoneOver100mFromTraffic = inputs.pctZoneOver100mFromTraffic ?? d.pctZoneOver100mFromTraffic;
  const greenBufferScore = inputs.greenBufferScore ?? d.greenBufferScore;
  const pctZoneOver65dB = inputs.pctZoneOver65dB ?? d.pctZoneOver65dB;
  const pctQuietAreas = inputs.pctQuietAreas ?? d.pctQuietAreas;
  const soundEnvironmentQuality = inputs.soundEnvironmentQuality ?? d.soundEnvironmentQuality;
  const mixedUseIndex = inputs.mixedUseIndex ?? d.mixedUseIndex;
  const groundPlaneActivationPct = inputs.groundPlaneActivationPct ?? d.groundPlaneActivationPct;
  const socialInfraPerThousand = inputs.socialInfraPerThousand ?? d.socialInfraPerThousand;
  const streetIntelligibility = inputs.streetIntelligibility ?? d.streetIntelligibility;
  const landmarkScore = inputs.landmarkScore ?? d.landmarkScore;
  const facadeDiversityIndex = inputs.facadeDiversityIndex ?? d.facadeDiversityIndex;
  const streetscapeVisualInterest = inputs.streetscapeVisualInterest ?? d.streetscapeVisualInterest;

  // Step 1: Calculate individual pathway scores
  const pathwayScores = {
    movement: calculateMovement(walkScore, bikeInfrastructure, blockPermeability),
    nature: calculateNature(treeCanopyPct, greenSpaceAccess300m, permeableSurfacePct),
    daylight: calculateDaylight(skyViewFactor, sunlightHoursGroundPlane),
    enrichment: calculateEnrichment(mixedUseIndex, groundPlaneActivationPct, socialInfraPerThousand),
    wayfinding: calculateWayfinding(streetIntelligibility, landmarkScore),
    air_quality: calculateAirQuality(pm25Annual, pctZoneOver100mFromTraffic, greenBufferScore),
    noise: calculateNoise(pctZoneOver65dB, pctQuietAreas, soundEnvironmentQuality),
    visual: calculateVisualEnvironment(facadeDiversityIndex, streetscapeVisualInterest),
  };

  // Step 2: Aggregate
  let positiveTotal = POSITIVE_PATHWAYS.reduce(
    (sum, pw) => sum + pathwayScores[pw] * p.PATHWAY_WEIGHTS[pw], 0
  );

  let negativeTotal = NEGATIVE_PATHWAYS.reduce(
    (sum, pw) => sum + pathwayScores[pw] * p.PATHWAY_WEIGHTS[pw], 0
  );

  // Synergy bonus
  const synergyBonus = calculatePathwaySynergy(pathwayScores);
  positiveTotal += synergyBonus;

  const netRaw = positiveTotal + negativeTotal;

  // Step 3: Overall score (0-1)
  const overallScore = clamp(netRaw);

  // Step 4: Regional scores
  const regional = calculateRegionalScores(pathwayScores);

  // Step 5: Interpretation
  const interpretation = interpretResults(overallScore, regional.dorsal, regional.ventral, pathwayScores);

  return {
    overallScore,
    dorsalScore: regional.dorsal,
    ventralScore: regional.ventral,
    pathwayScores,
    positiveTotal,
    negativeTotal,
    netRaw,
    synergyBonus,
    interpretation,
  };
}

/**
 * Assess multiple zones as a district (analogous to journey assessment).
 * Population-weighted average across zones.
 * @param {Array} zones - Array of zone input objects
 * @returns {Object} District assessment with per-zone and aggregate results
 */
export function assessDistrict(zones) {
  const zoneResults = zones.map(zone => {
    const result = assessZone(zone);
    return {
      name: zone.name || 'Unnamed Zone',
      zoneType: zone.zoneType || 'district',
      estimatedPopulation: zone.estimatedPopulation || 1000,
      areaAcres: zone.areaAcres || 10,
      ...result,
    };
  });

  // Population-weighted average
  const totalPop = zoneResults.reduce((s, r) => s + r.estimatedPopulation, 0);
  let weightedOverall = 0;
  let weightedDorsal = 0;
  let weightedVentral = 0;
  const aggregatedPathwayScores = {};

  if (totalPop > 0) {
    const allPathways = Object.keys(zoneResults[0].pathwayScores);
    for (const pw of allPathways) {
      aggregatedPathwayScores[pw] = 0;
    }

    for (const r of zoneResults) {
      const w = r.estimatedPopulation / totalPop;
      weightedOverall += r.overallScore * w;
      weightedDorsal += r.dorsalScore * w;
      weightedVentral += r.ventralScore * w;
      for (const pw of allPathways) {
        aggregatedPathwayScores[pw] += (r.pathwayScores[pw] || 0) * w;
      }
    }
  }

  const interpretation = interpretResults(
    weightedOverall, weightedDorsal, weightedVentral, aggregatedPathwayScores
  );

  return {
    zones: zoneResults,
    overallScore: weightedOverall,
    dorsalScore: weightedDorsal,
    ventralScore: weightedVentral,
    pathwayScores: aggregatedPathwayScores,
    interpretation,
    totalPopulation: totalPop,
    totalArea: zoneResults.reduce((s, r) => s + (r.areaAcres || 0), 0),
    zoneCount: zones.length,
  };
}

/**
 * Compare baseline vs improved scenarios.
 */
export function compareScenarios(baseline, improved) {
  const baseResult = assessZone(baseline);
  const improvedResult = assessZone(improved);
  const improvement = improvedResult.overallScore - baseResult.overallScore;
  const pctChange = baseResult.overallScore > 0
    ? (improvement / baseResult.overallScore * 100) : 0;

  const pathwayChanges = {};
  for (const pw of Object.keys(baseResult.pathwayScores)) {
    pathwayChanges[pw] = improvedResult.pathwayScores[pw] - baseResult.pathwayScores[pw];
  }

  return {
    baselineScore: baseResult.overallScore,
    improvedScore: improvedResult.overallScore,
    absoluteImprovement: improvement,
    percentImprovement: pctChange,
    pathwayChanges,
    baselineResult: baseResult,
    improvedResult: improvedResult,
  };
}

// ── Interpretation helpers ──

function getOverallRating(score) {
  if (score >= 0.85) return 'Excellent urban brain health support';
  if (score >= 0.70) return 'Good urban brain health support';
  if (score >= 0.55) return 'Moderate urban brain health support';
  if (score >= 0.40) return 'Below average urban brain health support';
  if (score >= 0.25) return 'Poor urban brain health support';
  return 'Very poor urban brain health support';
}

function getPercentileEstimate(score) {
  if (score >= 0.90) return 95;
  if (score >= 0.80) return 85;
  if (score >= 0.70) return 70;
  if (score >= 0.60) return 55;
  if (score >= 0.50) return 45;
  if (score >= 0.40) return 35;
  if (score >= 0.30) return 20;
  return 10;
}

function interpretResults(overallScore, dorsalScore, ventralScore, pathwayScores) {
  const rating = getOverallRating(overallScore);
  const percentile = getPercentileEstimate(overallScore);

  const strengths = [];
  const concerns = [];
  const recommendations = [];

  // Strengths
  if ((pathwayScores.movement || 0) >= 0.7) strengths.push('Excellent walkability and active transport');
  if ((pathwayScores.nature || 0) >= 0.7) strengths.push('Strong green infrastructure and canopy');
  if ((pathwayScores.daylight || 0) >= 0.7) strengths.push('Good solar access and sky exposure');
  if ((pathwayScores.enrichment || 0) >= 0.7) strengths.push('Vibrant mixed-use and social infrastructure');
  if ((pathwayScores.noise || 0) >= -0.05) strengths.push('Quiet sound environment');
  if ((pathwayScores.air_quality || 0) >= -0.05) strengths.push('Clean air quality');

  // Concerns
  if ((pathwayScores.movement || 0) < 0.4) concerns.push('Limited walkability and pedestrian infrastructure');
  if ((pathwayScores.nature || 0) < 0.4) concerns.push('Insufficient tree canopy and green space');
  if ((pathwayScores.daylight || 0) < 0.4) concerns.push('Poor solar access due to building massing');
  if ((pathwayScores.enrichment || 0) < 0.4) concerns.push('Low mixed-use diversity and activation');
  if ((pathwayScores.noise || 0) < -0.15) concerns.push('Elevated traffic and environmental noise');
  if ((pathwayScores.air_quality || 0) < -0.15) concerns.push('Air quality concerns from traffic or industry');
  if ((pathwayScores.visual || 0) < -0.1) concerns.push('Monotonous visual environment');

  // Recommendations
  const opportunities = [];
  if ((pathwayScores.nature || 0) < 0.7) opportunities.push({ gain: (0.85 - (pathwayScores.nature || 0)) * 0.18, msg: 'Increase tree canopy to 25-40% and ensure green space within 300m' });
  if ((pathwayScores.movement || 0) < 0.7) opportunities.push({ gain: (0.85 - (pathwayScores.movement || 0)) * 0.15, msg: 'Improve pedestrian network connectivity and add bike infrastructure' });
  if ((pathwayScores.air_quality || 0) < -0.1) opportunities.push({ gain: Math.abs(pathwayScores.air_quality || 0) * 0.18, msg: 'Add green buffers along traffic corridors and reduce PM2.5 exposure' });
  if ((pathwayScores.enrichment || 0) < 0.7) opportunities.push({ gain: (0.85 - (pathwayScores.enrichment || 0)) * 0.12, msg: 'Activate ground-plane uses and increase mixed-use diversity' });
  if ((pathwayScores.noise || 0) < -0.1) opportunities.push({ gain: Math.abs(pathwayScores.noise || 0) * 0.12, msg: 'Add noise barriers and expand quiet areas with natural soundscapes' });

  opportunities.sort((a, b) => b.gain - a.gain);
  recommendations.push(...opportunities.slice(0, 3).map(o => o.msg));

  if (overallScore >= 0.75 && recommendations.length === 0) {
    recommendations.push('District is well-optimized for brain health; maintain green infrastructure and walkability');
  }

  const regionalSummary = [];
  if (dorsalScore > ventralScore + 0.1) {
    regionalSummary.push('Environment favors cognitive over emotional circuit support');
  } else if (ventralScore > dorsalScore + 0.1) {
    regionalSummary.push('Environment favors emotional regulation over cognitive circuit');
  } else {
    regionalSummary.push('Balanced support for cognitive and emotional circuits');
  }

  return {
    overall: `${rating} (${percentile}th percentile estimate)`,
    rating,
    overallScore,
    percentile,
    strengths,
    concerns,
    recommendations,
    regional: {
      dorsal: `Cognitive circuit: ${getOverallRating(dorsalScore)}`,
      ventral: `Emotional circuit: ${getOverallRating(ventralScore)}`,
      summary: regionalSummary,
    },
  };
}
